import type { TPartImage, TPartInteractionWithImage } from "~/database/types";

import type { SessionCacheData } from "../hooks/use-session";

/**
 * Creates an updater function for TanStack Query's setQueryData.
 * This updater modifies a part within the session cache to set its default image.
 *
 * @param partId The ID of the part to update.
 * @param newDefaultImageId The ID of the new default image.
 * @param newImageObject The new image object (TPartImage) to associate with the part.
 * @param condition An optional callback that receives the part being considered for update.
 *                  If the callback returns false, the part is not updated.
 * @returns A function that can be passed to queryClient.setQueryData.
 */
export function createPartDefaultImageUpdater(
    partId: string,
    newDefaultImageId: string,
    newImageObject: TPartImage, // The image object itself, assumed to be TPartImage
    condition?: (partInteraction: TPartInteractionWithImage) => boolean,
) {
    return (
        oldData: SessionCacheData | undefined,
    ): SessionCacheData | undefined => {
        if (!oldData || !oldData.parts || !oldData.parts.present) {
            return oldData;
        }

        let partActuallyUpdated = false;
        const updatedPartsPresent = oldData.parts.present.map(
            (partInteraction) => {
                if (partInteraction.id === partId) {
                    // If a condition is provided, check it before updating
                    if (condition && !condition(partInteraction)) {
                        return partInteraction; // Condition not met, don't update this part interaction
                    }
                    partActuallyUpdated = true;
                    return {
                        ...partInteraction,
                        defaultImageId: newDefaultImageId,
                        // Assuming TPartInteractionWithImage has a field like 'part_image' or similar
                        // to store the actual default image object, matching user's previous edits.
                        part_image: newImageObject,
                    };
                }
                return partInteraction;
            },
        );

        if (!partActuallyUpdated) {
            return oldData; // No part met the criteria for update or was found
        }

        return {
            ...oldData,
            parts: {
                ...oldData.parts,
                present: updatedPartsPresent,
            },
        };
    };
}
